<template>
  <ContentfulColumns
    v-if="transformedHotels"
    :title="String(title)"
    :titleHeadlineLevel="2"
    :columns="transformedHotels"
    :columnsPerView="4"
    :tabs="showSliderTabs"
    :view-all-text="callToAction?.[0].fields?.label"
    :view-all-url="callToAction?.[0].fields?.link?.fields?.slug"
  />
</template>

<script setup lang="ts">
import type { TypeLocationCollection } from "@citizenm/contentful/shared/types/marketing/generated";

const { hotelsByContinentAndCity } = useHotels();

const { t, locale } = useI18n();
const localizedCityPath = useLocalizedCityPath();
const localizedHotelPath = useLocalizedHotelPath();

const { locations } = defineProps<{
  locations?: TypeLocationCollection<"WITHOUT_LINK_RESOLUTION">["fields"]["locations"];
  title: TypeLocationCollection<"WITHOUT_LINK_RESOLUTION">["fields"]["title"];
  callToAction: TypeLocationCollection<"WITHOUT_LINK_RESOLUTION">["fields"]["callToAction"];
}>();

const showSliderTabs = computed(() => {
  // By using no location (value of undefined) - all worldwide locations will be selected and the continents tab will be in place.
  return locations === undefined;
});

const transformedHotels = computed(() => {
  return Object.values(hotelsByContinentAndCity.value).flatMap((continent) =>
    Object.values(continent.cities).map((city) => {
      const firstHotel = city.hotels[0];
      const hotelLink = firstHotel.crsEnabled
        ? createMarriottHotelLink(firstHotel.crsCode, locale.value)
        : localizedHotelPath(firstHotel.slug);

      return {
        title: city.title,
        tab: showSliderTabs.value ? continent.title : undefined,
        text: t("website.locationcollection.hotelspercity.count.label", {
          count: city.hotels.length,
        }),
        image: city.hotels.length > 1 ? firstHotel.heroImage : city.heroImage,
        buttonUrl:
          city.hotels.length > 1
            ? localizedCityPath(firstHotel.slug)
            : hotelLink,
        buttonText: t(
          "website.locationcollection.card.city.hotels.link.label",
          {
            count: city.hotels.length,
          },
        ),
        buttonVariant: "tertiary",
      };
    }),
  );
});
</script>
